import React, { useState } from 'react';
import { useApi, fmtNum, fmtPrice, holdOr } from '../hooks/useApi';
import BotAvatar from './BotAvatar';

export function cashNotice(data) {
  if (data?.broker_verified !== false || data?.cash_currency !== 'USD') return null;
  return data.cash_reason === 'mock_usd_cash_unavailable'
    ? '브로커 미검증(모의 USD 현금 원천 없음)'
    : '브로커 미검증(USD 현금 원장 기준)';
}

const BOT_COLORS = {
  'PORT_WB': 'border-blue-500',
  'PORT_PL': 'border-green-500',
  'PORT_RD': 'border-purple-500',
  'PORT_CW': 'border-pink-500',
  'PORT_JL': 'border-yellow-500',
  'PORT_JOINT': 'border-gray-500',
};

export default function PortfolioSummary({ portfolios, loading }) {
  const [selected, setSelected] = useState(null);

  if (loading || !portfolios) {
    return <div className="animate-pulse h-40 bg-gray-800 rounded-lg" />;
  }

  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-400 mb-2">포트폴리오 개요</h2>
      {/* 모바일 가로스크롤 + 데스크탑 그리드 */}
      <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto pb-2 snap-x snap-mandatory md:overflow-x-visible md:pb-0">
        {portfolios.map((p) => (
          <PortfolioCard
            key={p.portfolio_id}
            portfolio={p}
            isSelected={selected === p.portfolio_id}
            onClick={() => setSelected(selected === p.portfolio_id ? null : p.portfolio_id)}
          />
        ))}
      </div>
      {selected && <PortfolioDetail portfolioId={selected} />}
    </div>
  );
}

function PortfolioCard({ portfolio: p, isSelected, onClick }) {
  const borderColor = BOT_COLORS[p.portfolio_id] || 'border-gray-600';
  const absoluteOnly = ['absolute_only', 'excluded'].includes(p.return_basis);
  const held = p.quality === 'UNKNOWN' || p.total_value == null;
  const returnPct = held || absoluteOnly || p.pnl_pct == null ? null : Number(p.pnl_pct).toFixed(2);
  const isPositive = Number(absoluteOnly ? p.pnl : returnPct) >= 0;
  const drawdownPct = p.peak_capital > 0 && p.total_value != null
    ? Math.min(0, (p.total_value / p.peak_capital) - 1) * 100
    : null;

  return (
    <div
      onClick={onClick}
      className={`bg-gray-800 rounded-lg p-3 md:p-4 border-l-4 ${borderColor} cursor-pointer
        hover:bg-gray-750 transition ${isSelected ? 'ring-1 ring-blue-500' : ''}
        min-w-[260px] md:min-w-0 snap-start flex-shrink-0 md:flex-shrink`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          <BotAvatar botName={p.bot_name} size="sm" />
          <div>
            <div className="font-semibold text-sm">{p.bot_name}</div>
            <div className="text-xs text-gray-500">{p.portfolio_id}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {p.is_suspended && (
            <span className="text-xs bg-red-900 text-red-300 px-1.5 py-0.5 rounded">정지</span>
          )}
          <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">
            {p.allowed_markets}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">현금 (KRW)</span>
          <div className="font-medium">{holdOr(p.current_cash, (v) => `₩${fmt(v)}`)}</div>
        </div>
        <div>
          <span className="text-gray-500">현금 (USD)</span>
          <div className="font-medium">{holdOr(p.current_cash_usd, (v) => `$${fmt(v)}`)}</div>
          {cashNotice(p) && <div className="text-yellow-300">{cashNotice(p)}</div>}
        </div>
        <div>
          <span className="text-gray-500">평가액 / 보유 포지션 평가액</span>
          <div className="font-medium">{holdOr(p.total_value, (v) => `₩${fmt(v)}`)}</div>
          {cashNotice(p) && <div className="text-yellow-300">{cashNotice(p)}</div>}
          <div className="text-gray-500">{holdOr(p.positions_value, (v) => `₩${fmt(v)}`)}</div>
        </div>
        <div>
          <span className="text-gray-500">손익 / 수익률</span>
          <div className={`font-medium ${held ? 'text-yellow-300' : isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {holdOr(p.pnl, (v) => `${v >= 0 ? '+' : ''}₩${fmt(v)}`)}
            <span className="text-gray-400"> / </span>
            {absoluteOnly ? '—' : holdOr(returnPct, (v) => `${Number(v) >= 0 ? '+' : ''}${v}%`)}
          </div>
        </div>
        <div>
          <span className="text-gray-500" title={p.peak_basis}>관측 고점 대비 (자본이전 조정)</span>
          <div className="font-medium">{holdOr(drawdownPct, (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`)}</div>
        </div>
        <div>
          <span className="text-gray-500">포지션</span>
          <div className="font-medium">{holdOr(p.position_count, (v) => `${v}개`)}</div>
        </div>
      </div>
      <div className="mt-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>가중치</span>
          <span>{holdOr(p.joint_weight, (v) => `${Number(v).toFixed(2)}%`)}</span>
        </div>
        {p.joint_weight == null
          ? <div className="text-[10px] text-gray-500">가중치 산출 데이터 없음</div>
          : <div className="w-full bg-gray-700 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(p.joint_weight * 2.5, 100)}%` }} />
          </div>}
      </div>
    </div>
  );
}

function PortfolioDetail({ portfolioId }) {
  const { data, loading } = useApi(`/api/account/portfolios/${portfolioId}`);

  if (loading || !data) {
    return <div className="animate-pulse h-32 bg-gray-800 rounded-lg mt-3" />;
  }

  if (data.quality === 'UNKNOWN') {
    return <div className="bg-gray-800 rounded-lg p-4 mt-3 text-yellow-300">보유 현황 산출 보류 — {data.reason}</div>;
  }

  if (!data.positions || data.positions.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 mt-3 border border-gray-700 text-sm text-gray-500">
        보유 포지션 없음
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4 mt-3 border border-gray-700 overflow-x-auto">
      <h3 className="text-sm font-semibold mb-2">{data.bot_name} — 보유 포지션</h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b border-gray-700">
            <th className="text-left py-1 px-2">종목</th>
            <th className="text-right py-1 px-2">수량</th>
            <th className="text-right py-1 px-2">평균가</th>
            <th className="text-right py-1 px-2">현재가</th>
            <th className="text-right py-1 px-2">목표가</th>
            <th className="text-right py-1 px-2">손절가</th>
            <th className="text-right py-1 px-2">손익</th>
            <th className="text-right py-1 px-2">수익률</th>
            <th className="text-center py-1 px-2">시장</th>
          </tr>
        </thead>
        <tbody>
          {data.positions.map((pos, i) => {
            const pnlColor = pos.unrealized_pnl == null ? 'text-yellow-300' : pos.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400';
            const targetPct = pos.target_price && pos.current_price
              ? ((pos.target_price - pos.current_price) / pos.current_price * 100) : null;
            const stopPct = pos.stop_loss && pos.current_price
              ? ((pos.stop_loss - pos.current_price) / pos.current_price * 100) : null;
            return (
              <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-750">
                <td className="py-1.5 px-2">
                  <div className="font-medium">{pos.ticker}</div>
                  <div className="text-gray-500">{pos.stock_name}</div>
                </td>
                <td className="text-right py-1.5 px-2">{pos.quantity}</td>
                <td className="text-right py-1.5 px-2">{fmtPrice(pos.avg_price, pos.market)}</td>
                <td className="text-right py-1.5 px-2">{fmtPrice(pos.current_price, pos.market)}</td>
                <td className="text-right py-1.5 px-2">
                  {pos.target_price ? (
                    <div>
                      <div className="text-green-400">{fmtPrice(pos.target_price, pos.market)}</div>
                      {targetPct != null && <div className="text-gray-500 text-[10px]">+{targetPct.toFixed(1)}%</div>}
                    </div>
                  ) : <span className="text-gray-600">-</span>}
                </td>
                <td className="text-right py-1.5 px-2">
                  {pos.stop_loss ? (
                    <div>
                      <div className="text-red-400">{fmtPrice(pos.stop_loss, pos.market)}</div>
                      {stopPct != null && <div className="text-gray-500 text-[10px]">{stopPct.toFixed(1)}%</div>}
                    </div>
                  ) : <span className="text-gray-600">-</span>}
                </td>
                <td className={`text-right py-1.5 px-2 ${pnlColor}`}>{holdOr(pos.unrealized_pnl, (v) => fmtPrice(v, pos.market))}</td>
                <td className={`text-right py-1.5 px-2 ${pnlColor}`}>
                  {holdOr(pos.unrealized_pnl_pct, (v) => `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`)}
                </td>
                <td className="text-center py-1.5 px-2">
                  <span className={`px-1 py-0.5 rounded text-xs ${
                    pos.market === 'US' ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-300'
                  }`}>{pos.market}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const fmt = fmtNum;
